import { useEffect, useState } from "react";
import { FlaskConical } from "lucide-react";
import { api } from "../lib/api";
import { apiError } from "../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { NeonButton } from "./common";

const Score = ({ k, v, c, testid }) => (
  <div className="border border-white/10 p-3"><p className="label-xs">{k}</p><p className="font-mono text-2xl font-extrabold mt-1" style={{ color: c }} data-testid={testid}>{v ?? "—"}{v != null && "%"}</p></div>
);

export const RepresentmentSimulator = ({ disputeId, label = "Simulate next representment" }) => {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(disputeId);
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      let id = disputeId;
      if (!id) {
        const { data } = await api.get("/command/summary");
        id = (data.active.find((d) => d.draft_status) || data.active[0])?.id;
      }
      setTarget(id);
      if (!id) return;
      const { data } = await api.get(`/disputes/${id}`);
      setText((data.representments || [])[0]?.content || "");
    })().catch((e) => setErr(apiError(e)));
  }, [open, disputeId]);

  const run = () => api.post("/representments/simulate", { dispute_id: target, text }).then((r) => { setRes(r.data); setErr(""); }).catch((e) => setErr(apiError(e)));

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setRes(null); }}>
      <DialogTrigger asChild>
        <button className="flex items-center gap-2 px-4 py-2.5 border border-accent2/50 text-accent2 font-mono text-xs tracking-wider hover:bg-accent2/10 transition-colors" title="Test your draft before submitting. See the predicted win rate." data-testid="simulate-representment-btn">
          <FlaskConical className="w-4 h-4" /> {label.toUpperCase()}
        </button>
      </DialogTrigger>
      <DialogContent className="bg-canvas border-white/15 max-w-2xl" data-testid="simulator-dialog">
        <DialogHeader><DialogTitle className="font-mono">Representment simulator · dry run</DialogTitle></DialogHeader>
        <p className="text-xs text-white/50">Edit the draft and re-simulate to see how the predicted win rate changes. Nothing is submitted. Dispute <span className="font-mono text-white/70" data-testid="simulator-dispute-id">{target || "…"}</span></p>
        {err && <p className="text-sm text-[#F87171]">{err}</p>}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} className="w-full bg-field border border-fieldline p-3 font-mono text-xs focus:outline-none focus:border-brand" placeholder="Paste or write a representment letter…" data-testid="simulator-textarea" />
        <NeonButton onClick={run} disabled={!target || !text.trim()} data-testid="simulator-run-btn">Simulate</NeonButton>
        {res && (
          <div className="space-y-3" data-testid="simulator-result">
            <div className="grid grid-cols-3 gap-3">
              <Score k="Current draft" v={res.current_draft} c="#fff" testid="sim-current" />
              <Score k="This version" v={res.predicted} c="var(--brand)" testid="sim-predicted" />
              <div className="border border-white/10 p-3"><p className="label-xs">Change</p><p className={`font-mono text-2xl font-extrabold mt-1 ${res.delta >= 0 ? "text-brand" : "text-[#F87171]"}`} data-testid="sim-delta">{res.delta == null ? "—" : `${res.delta >= 0 ? "+" : ""}${res.delta}`}</p></div>
            </div>
            <p className="text-sm text-white/70" data-testid="sim-summary">{res.delta == null ? `Predicted win rate: ${res.predicted}%.` : res.delta >= 0 ? `Your edit improves the predicted win rate to ${res.predicted}%.` : `This version lowers the predicted win rate to ${res.predicted}%.`} It cites {res.coverage_pct}% of the evidence you hold.</p>
            {res.tips.length > 0 && <ul className="text-xs text-[#FFB800] space-y-1" data-testid="sim-tips">{res.tips.map((t) => <li key={t}>→ {t}</li>)}</ul>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
